import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faStar, faCalendar, faClock, faPlay, faChevronLeft,
  faChevronRight, faExpand, faCompress, faTimes
} from '@fortawesome/free-solid-svg-icons';
import { Header, MediaCard, PageLoader, EpisodeSkeleton } from './shared';
import { useTMDB } from '../hooks/useFetch';
import { useDevice } from '../hooks/useMediaQuery';
import { IMAGE_SIZES, VIDEO_SOURCE_URL } from '../config/constants';

const MediaDetails: React.FC = () => {
  const { id, type } = useParams<{ id: string; type: string }>();
  const navigate = useNavigate();
  const { isMobileOrTablet, isTV } = useDevice();
  const { fetchMediaDetails, fetchSeasonDetails, fetchActorCredits } = useTMDB();

  // State
  const [mediaData, setMediaData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [selectedSeason, setSelectedSeason] = useState(1);
  const [selectedEpisode, setSelectedEpisode] = useState(1);
  const [seasonData, setSeasonData] = useState<any>(null);
  const [currentCastPage, setCurrentCastPage] = useState(1);
  const [trailerKey, setTrailerKey] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showPlayer, setShowPlayer] = useState(true);

  const CAST_PER_PAGE = isTV ? 8 : isMobileOrTablet ? 4 : 6;

  // Fetch media details
  useEffect(() => {
    const loadMedia = async () => {
      try {
        setLoading(true);
        const data = await fetchMediaDetails(type as 'movie' | 'tv', Number(id));
        setMediaData(data);

        // Get trailer
        const trailers = data.videos?.results?.filter(
          (v: any) => v.type === 'Trailer' && v.site === 'YouTube'
        );
        if (trailers?.length > 0) {
          setTrailerKey(trailers[0].key);
        }

        // Load first season for TV shows
        if (type === 'tv' && data.seasons?.length > 0) {
          const firstSeason = data.seasons.find((s: any) => s.season_number > 0);
          if (firstSeason) {
            setSelectedSeason(firstSeason.season_number);
            const season = await fetchSeasonDetails(Number(id), firstSeason.season_number);
            setSeasonData(season);
          }
        }
      } catch (error) {
        console.error('Error loading media:', error);
      } finally {
        setLoading(false);
      }
    };

    loadMedia();
    window.scrollTo(0, 0);
  }, [id, type, fetchMediaDetails, fetchSeasonDetails]);

  // Fetch season details when season changes
  useEffect(() => {
    const loadSeason = async () => {
      if (type === 'tv' && mediaData) {
        try {
          const season = await fetchSeasonDetails(Number(id), selectedSeason);
          setSeasonData(season);
          setSelectedEpisode(1);
        } catch (error) {
          console.error('Error loading season:', error);
        }
      }
    };

    if (mediaData) loadSeason();
  }, [selectedSeason, id, type, mediaData, fetchSeasonDetails]);

  const handleActorClick = useCallback(async (actorId: number) => {
    try {
      const films = await fetchActorCredits(actorId);
      navigate(`/actor/${actorId}`, { state: { films } });
    } catch (error) {
      console.error('Error fetching actor credits:', error);
    }
  }, [fetchActorCredits, navigate]);

  if (loading) return <PageLoader />;
  if (!mediaData) return null;

  const {
    title, name, overview, vote_average, credits,
    release_date, first_air_date, runtime, genres,
    backdrop_path, poster_path, number_of_seasons,
    similar, recommendations
  } = mediaData;

  const mediaTitle = title || name;
  const releaseYear = release_date
    ? new Date(release_date).getFullYear()
    : first_air_date
      ? new Date(first_air_date).getFullYear()
      : null;

  const backdropUrl = backdrop_path
    ? `${IMAGE_SIZES.backdrop.original}${backdrop_path}`
    : null;
  const posterUrl = poster_path
    ? `${IMAGE_SIZES.poster.large}${poster_path}`
    : null;

  // Video URL
  const videoUrl = type === 'tv'
    ? `${VIDEO_SOURCE_URL}/tv/${id}/${selectedSeason}/${selectedEpisode}`
    : `${VIDEO_SOURCE_URL}/movie/${id}`;

  // Cast pagination
  const totalCastPages = Math.ceil((credits?.cast?.length || 0) / CAST_PER_PAGE);
  const currentCast = credits?.cast?.slice(
    (currentCastPage - 1) * CAST_PER_PAGE,
    currentCastPage * CAST_PER_PAGE
  ) || [];

  // Related content
  const relatedContent = [...(recommendations?.results || []), ...(similar?.results || [])]
    .filter((item, index, self) => self.findIndex(i => i.id === item.id) === index)
    .slice(0, 12);

  return (
    <div className="min-h-screen bg-dark-950">
      <Header showSearch={false} transparent={true} />

      {/* Hero Section with Backdrop */}
      <section className="relative -mt-16 sm:-mt-18 lg:-mt-20 xl:-mt-24 2xl:-mt-28">
        {/* Backdrop Image */}
        <div className="absolute inset-0 h-[50vh] sm:h-[60vh] lg:h-[70vh]">
          {backdropUrl && (
            <img
              src={backdropUrl}
              alt={mediaTitle}
              className="w-full h-full object-cover"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-dark-950 via-dark-950/80 to-dark-950/50" />
          <div className="absolute inset-0 bg-gradient-to-r from-dark-950 via-transparent to-transparent" />
        </div>

        {/* Content */}
        <div className="relative z-10 pt-24 sm:pt-32 lg:pt-40 xl:pt-48 
          max-w-[2200px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 2xl:px-16">

          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
            {/* Poster */}
            <div className="flex-shrink-0 mx-auto lg:mx-0">
              <div className="relative w-48 sm:w-56 md:w-64 lg:w-72 xl:w-80 
                rounded-2xl overflow-hidden shadow-2xl shadow-black/50
                ring-2 ring-white/10">
                {posterUrl ? (
                  <img
                    src={posterUrl}
                    alt={mediaTitle}
                    className="w-full aspect-poster object-cover"
                  />
                ) : (
                  <div className="w-full aspect-poster bg-dark-800 flex items-center justify-center">
                    <span className="text-gray-500">No poster</span>
                  </div>
                )}

                {/* Rating overlay */}
                {vote_average > 0 && (
                  <div className="absolute top-3 left-3 flex items-center gap-1.5
                    px-3 py-1.5 rounded-full bg-black/60 backdrop-blur-sm">
                    <FontAwesomeIcon icon={faStar} className="text-primary-400" />
                    <span className="font-bold">{vote_average.toFixed(1)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Info */}
            <div className="flex-grow text-center lg:text-left">
              {/* Title */}
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl
                font-black leading-tight mb-4 animate-fade-in">
                {mediaTitle}
                {releaseYear && (
                  <span className="text-gray-400 font-normal ml-4 text-2xl sm:text-3xl lg:text-4xl">
                    ({releaseYear})
                  </span>
                )}
              </h1>

              {/* Meta info */}
              <div className="flex flex-wrap items-center justify-center lg:justify-start 
                gap-3 sm:gap-4 mb-6 text-gray-400 text-sm sm:text-base">
                {type === 'tv' && number_of_seasons && (
                  <span className="flex items-center gap-2">
                    <span className="px-2 py-0.5 rounded bg-primary-500/20 text-primary-400 font-semibold">
                      {number_of_seasons} Season{number_of_seasons > 1 ? 's' : ''}
                    </span>
                  </span>
                )}
                {runtime && (
                  <span className="flex items-center gap-2">
                    <FontAwesomeIcon icon={faClock} className="text-primary-400" />
                    {Math.floor(runtime / 60)}h {runtime % 60}m
                  </span>
                )}
                {(release_date || first_air_date) && (
                  <span className="flex items-center gap-2">
                    <FontAwesomeIcon icon={faCalendar} className="text-primary-400" />
                    {release_date || first_air_date}
                  </span>
                )}
              </div>

              {/* Genres */}
              {genres?.length > 0 && (
                <div className="flex flex-wrap justify-center lg:justify-start gap-2 mb-6">
                  {genres.map((genre: any) => (
                    <span
                      key={genre.id}
                      className="px-3 py-1 rounded-full text-sm font-medium
                        bg-white/10 text-gray-300 border border-white/10"
                    >
                      {genre.name}
                    </span>
                  ))}
                </div>
              )}

              {/* Overview */}
              <p className="text-gray-300 text-sm sm:text-base lg:text-lg xl:text-xl
                max-w-3xl mx-auto lg:mx-0 leading-relaxed mb-8">
                {overview || 'No description available.'}
              </p>

              {/* Play button */}
              <button
                onClick={() => {
                  setShowPlayer(true);
                  setTimeout(() => {
                    document.getElementById('video-player')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }, 100);
                }}
                className="inline-flex items-center gap-3
                  px-8 sm:px-10 py-4 sm:py-5
                  bg-gradient-to-r from-primary-500 to-rose-500 rounded-full
                  font-bold text-lg sm:text-xl
                  transform transition-all duration-300
                  hover:scale-105 hover:shadow-glow-lg
                  focus:outline-none focus-visible:ring-4 focus-visible:ring-primary-500/50"
              >
                <FontAwesomeIcon icon={faPlay} />
                <span>Watch Now</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="relative z-10 max-w-[2200px] mx-auto 
        px-4 sm:px-6 lg:px-8 xl:px-12 2xl:px-16 
        py-12 sm:py-16 lg:py-20">

        {/* Video Player Section */}
        {showPlayer && (
          <section id="video-player" className="mb-12 sm:mb-16">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold">
                Now Playing
              </h2>
              <button
                onClick={() => setIsFullscreen(!isFullscreen)}
                className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
              >
                <FontAwesomeIcon icon={isFullscreen ? faCompress : faExpand} />
              </button>
            </div>

            <div className={`relative rounded-2xl overflow-hidden 
              bg-dark-900 shadow-2xl shadow-black/50
              ${isFullscreen ? 'fixed inset-4 z-50' : 'aspect-video'}`}
            >
              <iframe
                src={videoUrl}
                title={`Watch ${mediaTitle}`}
                className="w-full h-full"
                allowFullScreen
                allow="autoplay; encrypted-media; fullscreen"
              />

              {isFullscreen && (
                <button
                  onClick={() => setIsFullscreen(false)}
                  className="absolute top-4 right-4 p-3 rounded-full
                    bg-black/50 hover:bg-black/70 transition-colors"
                >
                  <FontAwesomeIcon icon={faTimes} className="text-xl" />
                </button>
              )}
            </div>
          </section>
        )}

        {/* TV Show Season/Episode Selector */}
        {type === 'tv' && mediaData.seasons && (
          <section className="mb-12 sm:mb-16">
            {/* Season Selector */}
            <div className="flex items-center gap-4 mb-6">
              <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold">
                Episodes
              </h2>
              <select
                value={selectedSeason}
                onChange={(e) => setSelectedSeason(Number(e.target.value))}
                className="px-4 py-2 rounded-xl bg-dark-800 border border-white/10
                  text-white font-medium focus:outline-none focus:ring-2 focus:ring-primary-500
                  cursor-pointer"
              >
                {mediaData.seasons
                  .filter((season: any) => season.season_number > 0)
                  .map((season: any) => (
                    <option key={season.id} value={season.season_number}>
                      Season {season.season_number}
                    </option>
                  ))}
              </select>
            </div>

            {/* Episode Grid */}
            {seasonData ? (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10 gap-3">
                {seasonData.episodes?.map((episode: any) => (
                  <button
                    key={episode.id}
                    onClick={() => setSelectedEpisode(episode.episode_number)}
                    className={`
                      relative p-4 rounded-xl font-medium
                      transition-all duration-300
                      ${selectedEpisode === episode.episode_number
                        ? 'bg-gradient-to-br from-primary-500 to-rose-500 text-white shadow-lg shadow-primary-500/30'
                        : 'bg-dark-800 text-gray-300 hover:bg-dark-700 hover:text-white'
                      }
                    `}
                  >
                    <span className="block text-lg font-bold">{episode.episode_number}</span>
                    {selectedEpisode === episode.episode_number && (
                      <FontAwesomeIcon icon={faPlay} className="absolute top-2 right-2 text-xs" />
                    )}
                  </button>
                ))}
              </div>
            ) : (
              <EpisodeSkeleton count={10} />
            )}

            {/* Selected Episode Info */}
            {seasonData?.episodes && (
              <div className="mt-6 p-6 rounded-2xl bg-dark-800/50 border border-white/5">
                {seasonData.episodes
                  .filter((ep: any) => ep.episode_number === selectedEpisode)
                  .map((episode: any) => (
                    <div key={episode.id}>
                      <div className="flex items-start justify-between gap-4 mb-3">
                        <h3 className="text-lg sm:text-xl font-bold">
                          Episode {episode.episode_number}: {episode.name}
                        </h3>
                        {episode.vote_average > 0 && (
                          <span className="flex items-center gap-1 text-primary-400">
                            <FontAwesomeIcon icon={faStar} />
                            {episode.vote_average.toFixed(1)}
                          </span>
                        )}
                      </div>
                      <p className="text-gray-400 text-sm sm:text-base">
                        {episode.overview || 'No description available.'}
                      </p>
                    </div>
                  ))}
              </div>
            )}
          </section>
        )}

        {/* Trailer Section */}
        {trailerKey && (
          <section className="mb-12 sm:mb-16">
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold mb-6">
              Official Trailer
            </h2>
            <div className="aspect-video rounded-2xl overflow-hidden
              bg-dark-900 shadow-xl shadow-black/30">
              <iframe
                src={`https://www.youtube.com/embed/${trailerKey}`}
                title="Trailer"
                className="w-full h-full"
                allowFullScreen
              />
            </div>
          </section>
        )}

        {/* Cast Section */}
        {credits?.cast?.length > 0 && (
          <section className="mb-12 sm:mb-16">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold">
                Cast
              </h2>
              {totalCastPages > 1 && (
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setCurrentCastPage(prev => Math.max(1, prev - 1))}
                    disabled={currentCastPage === 1}
                    className="p-2 rounded-lg bg-dark-800 disabled:opacity-40 
                      hover:bg-dark-700 transition-colors"
                  >
                    <FontAwesomeIcon icon={faChevronLeft} />
                  </button>
                  <span className="text-sm text-gray-400">
                    {currentCastPage} / {totalCastPages}
                  </span>
                  <button
                    onClick={() => setCurrentCastPage(prev => Math.min(totalCastPages, prev + 1))}
                    disabled={currentCastPage === totalCastPages}
                    className="p-2 rounded-lg bg-dark-800 disabled:opacity-40 
                      hover:bg-dark-700 transition-colors"
                  >
                    <FontAwesomeIcon icon={faChevronRight} />
                  </button>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 sm:gap-6">
              {currentCast.map((actor: any, index: number) => (
                <button
                  key={actor.id}
                  onClick={() => handleActorClick(actor.id)}
                  className="group text-center animate-fade-in"
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <div className="relative w-20 h-20 sm:w-24 sm:h-24 lg:w-28 lg:h-28 
                    mx-auto mb-3 rounded-full overflow-hidden
                    ring-2 ring-transparent group-hover:ring-primary-500
                    transition-all duration-300 group-hover:scale-105">
                    <img
                      src={actor.profile_path
                        ? `${IMAGE_SIZES.profile.medium}${actor.profile_path}`
                        : '/placeholder-actor.jpg'
                      }
                      alt={actor.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <p className="font-semibold text-sm sm:text-base group-hover:text-primary-400 transition-colors">
                    {actor.name}
                  </p>
                  <p className="text-xs sm:text-sm text-gray-500 line-clamp-1">
                    {actor.character}
                  </p>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* Related Content */}
        {relatedContent.length > 0 && (
          <section>
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold mb-6">
              You May Also Like
            </h2>
            <div className="media-grid">
              {relatedContent.map((item, index) => (
                <MediaCard
                  key={item.id}
                  item={{ ...item, media_type: item.media_type || type }}
                  index={index}
                  size="full"
                  showRating={true}
                />
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
};

export default MediaDetails;
